import { useEffect } from 'react';
import { ConsentService } from '../../services/ConsentService';

// Use environment variable for GTM ID
const GTM_ID = import.meta.env.VITE_GTM_ID || 'GTM-PPJRZT7C';

/**
 * Component to handle Google Tag Manager integration
 * Works with ConsentService to respect user's privacy preferences
 * 
 * NOTE: Default consent is now initialized in index.html before GTM script loads
 * for proper Consent Mode v2 integration. This component just loads the GTM script.
 */
export const GoogleTagManager = () => {
  useEffect(() => {
    // Make gtag available globally if it doesn't exist
    if (!window.gtag) {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function(...args: any[]) {
        window.dataLayer!.push(arguments);
      };
    }
    
    // Check for stored consent and apply it if available
    // Do NOT initialize default consent here - that's now in index.html
    const consentStatus = localStorage.getItem('cookieConsent');
    if (consentStatus === 'accepted') {
      ConsentService.acceptCookies('stored');
    } else if (consentStatus === 'rejected') {
      // No need to explicitly reject as default is already denied,
      // but we track it for analytics
      if (window.dataLayer) {
        window.dataLayer.push({
          'event': 'consent_update',
          'consent_status': 'rejected',
          'consent_source': 'stored'
        });
      }
    }
    
    // Load GTM script asynchronously
    const loadGTM = () => {
      // Check if GTM is already loaded
      if (document.querySelector('script[src*="googletagmanager.com/gtm.js"]')) {
        return;
      }
      
      const script = document.createElement('script');
      script.defer = true;
      script.setAttribute('data-load', 'analytics');
      script.innerHTML = `
        (function(w,d,s,l,i){
          w[l]=w[l]||[];
          var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),
          dl=l!='dataLayer'?'&l='+l:'';
          j.async=true;
          j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
          f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${GTM_ID}');
      `;
      
      document.head.appendChild(script);
    };

    // Only load GTM in production
    if (process.env.NODE_ENV === 'production') {
      // Load GTM immediately - consent is already set to denied by default
      // in index.html before this runs
      loadGTM();
    }

    // No cleanup needed - GTM should stay loaded
  }, []);

  if (process.env.NODE_ENV !== 'production') {
    return null;
  }

  return (
    <noscript>
      <iframe
        src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
        height="0"
        width="0"
        style={{ display: 'none', visibility: 'hidden' }}
      />
    </noscript>
  );
}; 